import oidcConfig, { getRedirecturl } from "../utils/oidcConfig";
import api from "../services/api";
import { jwtDecode } from "jwt-decode";
import { StorageService } from "../utils/storageService";
import { generateCodeChallenge } from "../utils/pkce";
import { generateCodeVerifier } from "../utils/pkce";

// Define a type for the API response
export interface ApiResponse<T> {
  data: T;
  error?: string;
}

// Function to handle GET requests
export const get = async <T>(url: string): Promise<ApiResponse<T>> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data: T = await response.json();
    return { data };
  } catch (error: any) {
    return { data: null as unknown as T, error: error.message };
  }
};


export const getAccessToken = async (code: string, codeVerifier: string) => {
  try {
    const redirectUrl = getRedirecturl() as string;
    // For localhost set directly 
    if (redirectUrl === "http://localhost:3000") {

      const access_token = "eyJhbGciOiJFUzI1NiIsInR5cCI6IkpXVCIsImd0eSI6WyJwYXNzd29yZCJdLCJraWQiOiJyWDk3d0dLU1NxdXpVS2NjUFVPS1B1bmwtbXMifQ.eyJhdWQiOiIyZWUyMTdmZi1kYjdiLTRiOWUtOWQ1Ny03MTYyYmZjYjEwZDMiLCJleHAiOjE3Njg5MzQ3MTQsImlhdCI6MTc2ODg5ODcxNCwiaXNzIjoic2NzZ2xvYmFsc2VydmljZXMuY29tIiwic3ViIjoiMGQ0ZDQ3MGUtYWQ4Ny00OTA3LThmY2EtNGYxZjdkZTI0NWMwIiwianRpIjoiMjAzYTU3YzAtODNlNi00YjUyLWI5YzctNGVkNmQ5NGJjNDRlIiwiYXV0aGVudGljYXRpb25UeXBlIjoiUEFTU1dPUkQiLCJlbWFpbCI6InVkYXkuZ29zaGlrYSthbmlyYUBtYWFudGljLmNvbSIsImVtYWlsX3ZlcmlmaWVkIjp0cnVlLCJwcmVmZXJyZWRfdXNlcm5hbWUiOiJ1ZGF5Lmdvc2hpa2ErYW5pcmFAbWFhbnRpYy5jb20iLCJhcHBsaWNhdGlvbklkIjoiMmVlMjE3ZmYtZGI3Yi00YjllLTlkNTctNzE2MmJmY2IxMGQzIiwicm9sZXMiOlsiQWRtaW4iXSwic2NvcGUiOiJvcGVuaWQiLCJhdXRoX3RpbWUiOjE3Njg4OTg3MTQsInRpZCI6ImUwZmVjNTBhLWQ1YmUtMjk4Yi1iZDIzLTc5YmY0NmEyNzI2YSIsImxhc3ROYW1lIjoiTG9nbyBQb3J0YWwgQ3VzdG9tZXIiLCJwcmVmZXJyZWRMYW5ndWFnZSI6ImVuIiwic2FsZXNmb3JjZUlkIjoiMDAzUnQwMDAwMG9zRnV2SUFFIiwiZmlyc3ROYW1lIjoiQW5pcmEifQ.NuiBnnQbmrb-P6ElScs4pLJ7s08C9f9oPwdTJqIW11dlhGVe919F2uRGX7dITeJsZ8rEYClLaO6iWEG6JNYt7A";
      const expires_in = 35999;
      const userId = "0d4d470e-ad87-4907-8fca-4f1f7de245c0";
      
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("userId", userId);
      localStorage.setItem(
        "access_token_expiry",
        (Date.now() + expires_in * 1000).toString()
      );
      const user: any = jwtDecode(access_token);
      return user;
    } else {
      const formData = new FormData();
      formData.append("client_id", oidcConfig.client_id);
      formData.append("code_verifier", codeVerifier);
      formData.append("code", code);
      formData.append("redirect_uri", oidcConfig.redirect_uri);
      formData.append("grant_type", "authorization_code");

      const url = "/oauth2/token";

      const { data } = await api.post(url, formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      const { access_token, expires_in, userId } = data;
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("userId", userId);
      localStorage.setItem(
        "access_token_expiry",
        (Date.now() + expires_in * 1000).toString()
      );
      const user: any = jwtDecode(access_token);
      return user;
    }
  } catch (error) {
    console.error("Error fetching token", error);
    throw error;
  }
};

/**
 * Logout user and navigate to login page
 */
export const logout = () => {
  StorageService.clearAll();
  localStorage.removeItem('access_token');
  sessionStorage.removeItem('code_verifier');
  const logouturl = `${oidcConfig.authority}/oauth2/logout/?client_id=${oidcConfig.client_id}&redirect_uri=${oidcConfig.logout_redirect_uri}`;
  window.location.href = logouturl;
};

export const login = async () => {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  localStorage.setItem("code_verifier", codeVerifier);
  const authorizationUrl = `${oidcConfig.authority}/oauth2/authorize/?client_id=${oidcConfig.client_id}&response_type=code&scope=${oidcConfig.scope}&redirect_uri=${oidcConfig.redirect_uri}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
  window.location.href = authorizationUrl;
};
